/**
 * Group Preset Registry
 *
 * Shared (API-serializable) group preset definitions — no ComponentType fields.
 * These replace the old display-mode and view-preset IDs with a unified set.
 */

// Entry Interface

export interface SharedGroupPresetEntry {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly layouts: readonly ('table' | 'cards' | 'kanban' | 'calendar')[];
}

// Registry

export const GROUP_PRESET_REGISTRY = [
  { id: 'default',        label: 'Default',         description: 'No named preset — ViewContext is the only constraint.', layouts: ['cards', 'table', 'kanban', 'calendar'] },
  { id: 'inbox',          label: 'Inbox Zero',      description: 'Progress tracking, clear-your-queue triage.',         layouts: ['table', 'cards'] },
  { id: 'dashboard',      label: 'Dashboard',       description: 'Dense columns with sort and filter.',                 layouts: ['table'] },
  { id: 'workflow',       label: 'Workflow',         description: 'Status-driven grouping with collapsible sections.',   layouts: ['table', 'cards'] },
  { id: 'priority_stack', label: 'Priority Stack',  description: 'Featured top item, compressed rest.',                 layouts: ['table', 'cards'] },
  { id: 'quick_glance',   label: 'Quick Glance',    description: 'Compact with expand on demand.',                      layouts: ['table', 'cards'] },
] as const satisfies readonly SharedGroupPresetEntry[];

// Derived Type

export type GroupPresetId = (typeof GROUP_PRESET_REGISTRY)[number]['id'];

// Lookup & Validation

export function getSharedGroupPresetEntry(id: string): SharedGroupPresetEntry | undefined {
  return (GROUP_PRESET_REGISTRY as readonly SharedGroupPresetEntry[]).find((e) => e.id === id);
}

export function isValidGroupPresetId(id: string): id is GroupPresetId {
  return GROUP_PRESET_REGISTRY.some((e) => e.id === id);
}

// Legacy ID Migration

const LEGACY_ID_MAP: Record<string, GroupPresetId> = {
  inbox_zero: 'inbox',
  kanban_lane: 'workflow',
  dashboard: 'dashboard',
  priority_stack: 'priority_stack',
  quick_glance: 'quick_glance',
  // Dropped modes → default
  command_center: 'default',
  batch_processor: 'default',
  batch: 'default',
  smart_bundle: 'default',
  action_matrix: 'default',
  split_panel: 'default',
  // Old display mode IDs (same as new where they exist)
  inbox: 'inbox',
} as const;

export function normalizeGroupPreset(input: string | null | undefined): GroupPresetId {
  if (!input) return 'default';
  if (isValidGroupPresetId(input)) return input;
  const mapped = LEGACY_ID_MAP[input];
  return mapped !== undefined ? mapped : 'default';
}
